/** Lightweight Store + Sponsors + Donate landing pages.
 *  Phase 1 = inquiry-only (no payment processor). All forms create CRM leads.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { ShoppingBag, Heart, Award, ArrowRight, Crown } from "lucide-react";

const STORE_ITEMS = [
  { id: "wb-finlit",   name: "Financial Literacy Workbook",       price: "$29",  cat: "Workbooks" },
  { id: "wb-civic",    name: "Civic Duty Workbook",                price: "$29",  cat: "Workbooks" },
  { id: "wb-entre",    name: "Entrepreneur Academy Workbook",      price: "$39",  cat: "Workbooks" },
  { id: "man-leader",  name: "Leadership Manual (SALS)",          price: "$49",  cat: "Manuals" },
  { id: "jr-leader",   name: "Crown Leadership Journal",           price: "$24",  cat: "Journals" },
  { id: "dl-pack",     name: "Civic Education Digital Bundle",     price: "$19",  cat: "Digital" },
  { id: "shirt",       name: "Crown Institute T-Shirt",            price: "$28",  cat: "Apparel" },
  { id: "hat",         name: "Crown Institute Cap",                price: "$22",  cat: "Apparel" },
  { id: "cert",        name: "Certificate Replacement",            price: "$15",  cat: "Records" },
];

export const Store = () => (
  <Layout>
    <SEOHead title="Crown Institute Store | AuditIQ" path="/store" />
    <Toaster position="top-right" richColors theme="dark" />
    <section className="container-x pt-20 pb-12">
      <div className="text-[11px] uppercase tracking-[0.28em] text-gold flex items-center gap-2">
        <ShoppingBag size={12} /> Crown Institute Store
      </div>
      <h1 className="font-display text-4xl md:text-5xl mt-3">Workbooks · Manuals · Journals · Apparel</h1>
      <p className="text-cream-dim mt-3 max-w-2xl">
        Browse Crown Institute Academy materials. Secure checkout will activate before public launch — for now, click any item to request a pre-order.
      </p>
    </section>
    <section className="container-x pb-20">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="store-grid">
        {STORE_ITEMS.map(it => (
          <article key={it.id} className="glass p-5 flex flex-col" data-testid={`store-item-${it.id}`}>
            <div className="aspect-video bg-navy-deep border border-gold-dim/20 rounded mb-4 flex items-center justify-center">
              <Crown size={32} className="text-gold/50" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-electric">{it.cat}</div>
            <div className="text-cream font-medium mt-1">{it.name}</div>
            <div className="font-display text-2xl text-gold mt-1">{it.price}</div>
            <Button onClick={() => toast.info("Pre-order requested. We will reach out before launch.")}
              className="btn-ghost-gold h-10 mt-4 text-xs" data-testid={`store-preorder-${it.id}`}>
              Request Pre-Order <ArrowRight size={12} className="ml-2" />
            </Button>
          </article>
        ))}
      </div>
    </section>
  </Layout>
);


export const Sponsors = () => {
  const [form, setForm] = useState({ contact_name: "", organization: "", email: "", phone: "", sponsorship_level: "", interest_area: "", message: "" });
  const [meta, setMeta] = useState({ sponsorship_levels: [] });

  useEffect(() => { api.get("/academy/meta").then(r => setMeta(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/academy/sponsor-inquiry", form);
      toast.success(r.data.message);
      setForm({ contact_name: "", organization: "", email: "", phone: "", sponsorship_level: "", interest_area: "", message: "" });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d
        : Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join(", ")
        : "Submit failed");
    }
  };

  return (
    <Layout>
      <SEOHead title="Sponsors | AuditIQ · Crown Institute" path="/sponsors" />
      <Toaster position="top-right" richColors theme="dark" />
      <section className="container-x pt-20 pb-12 max-w-4xl">
        <div className="text-[11px] uppercase tracking-[0.28em] text-gold flex items-center gap-2">
          <Award size={12} /> Sponsors
        </div>
        <h1 className="font-display text-4xl md:text-5xl mt-3">Empower the next generation of civic and entrepreneurial leaders.</h1>
        <p className="text-cream-dim mt-4 max-w-2xl">
          Crown Institute Academy welcomes corporate, civic, and individual sponsors. Your support funds workbooks,
          internships, certification pathways, and underwrites scholarships for students who could not otherwise attend.
        </p>
      </section>
      <section className="container-x pb-20 max-w-4xl">
        <form onSubmit={submit} className="glass p-6 grid md:grid-cols-2 gap-3" data-testid="sponsors-page-form">
          <Input required placeholder="Contact Name" value={form.contact_name} onChange={e => setForm({ ...form, contact_name: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="sponsors-contact" />
          <Input required placeholder="Organization" value={form.organization} onChange={e => setForm({ ...form, organization: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="sponsors-org" />
          <Input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="sponsors-email" />
          <Input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
          <Select value={form.sponsorship_level} onValueChange={v => setForm({ ...form, sponsorship_level: v })}>
            <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream md:col-span-2" data-testid="sponsors-level"><SelectValue placeholder="Sponsorship level" /></SelectTrigger>
            <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
              {(meta.sponsorship_levels || []).map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Interest area" value={form.interest_area} onChange={e => setForm({ ...form, interest_area: e.target.value })} className="bg-navy-deep border-gold-dim/40 md:col-span-2" />
          <Textarea rows={4} placeholder="Tell us about your sponsorship goals" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="bg-navy-deep border-gold-dim/40 text-cream md:col-span-2" />
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" className="btn-gold h-11 px-6" data-testid="sponsors-submit">Send Sponsor Inquiry</Button>
          </div>
        </form>
      </section>
    </Layout>
  );
};


export const Donate = () => {
  const [form, setForm] = useState({ donor_name: "", email: "", phone: "", cause: "", amount: "", message: "", recurring: false });
  const [meta, setMeta] = useState({ donation_causes: [] });

  useEffect(() => { api.get("/academy/meta").then(r => setMeta(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/academy/donation-inquiry", form);
      toast.success(r.data.message);
      setForm({ donor_name: "", email: "", phone: "", cause: "", amount: "", message: "", recurring: false });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d
        : Array.isArray(d) ? d.map(x => x?.msg || JSON.stringify(x)).join(", ")
        : "Submit failed");
    }
  };

  return (
    <Layout>
      <SEOHead title="Donate | AuditIQ · Crown Institute" path="/donate" />
      <Toaster position="top-right" richColors theme="dark" />
      <section className="container-x pt-20 pb-12 max-w-4xl">
        <div className="text-[11px] uppercase tracking-[0.28em] text-gold flex items-center gap-2">
          <Heart size={12} /> Donate
        </div>
        <h1 className="font-display text-4xl md:text-5xl mt-3">Give a student a future.</h1>
        <p className="text-cream-dim mt-4 max-w-2xl">
          Every gift directly funds Crown Institute Academy curriculum, internships, and certification pathways.
          Payment processing will go live before public launch — your inquiry is recorded today and our team will follow up
          with a tax-receipted giving link.
        </p>
        <Link to="/sponsors"><Button className="btn-ghost-gold h-10 px-4 text-xs mt-4">Become a Sponsor →</Button></Link>
      </section>
      <section className="container-x pb-20 max-w-3xl">
        <form onSubmit={submit} className="glass p-6 grid md:grid-cols-2 gap-3" data-testid="donate-page-form">
          <Input required placeholder="Your Name" value={form.donor_name} onChange={e => setForm({ ...form, donor_name: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="donate-page-name" />
          <Input required type="email" placeholder="Email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="bg-navy-deep border-gold-dim/40" data-testid="donate-page-email" />
          <Input placeholder="Phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
          <Input placeholder="Amount (USD)" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="bg-navy-deep border-gold-dim/40" />
          <Select value={form.cause} onValueChange={v => setForm({ ...form, cause: v })}>
            <SelectTrigger className="bg-navy-deep border-gold-dim/40 text-cream md:col-span-2" data-testid="donate-page-cause"><SelectValue placeholder="Select cause" /></SelectTrigger>
            <SelectContent className="bg-navy-soft border-gold-dim/40 text-cream">
              {(meta.donation_causes || []).map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-sm text-cream md:col-span-2">
            <input type="checkbox" checked={form.recurring} onChange={e => setForm({ ...form, recurring: e.target.checked })} data-testid="donate-page-recurring" />
            Make this a monthly gift
          </label>
          <Textarea rows={3} placeholder="Message (optional)" value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className="bg-navy-deep border-gold-dim/40 text-cream md:col-span-2" />
          <div className="md:col-span-2 flex justify-end">
            <Button type="submit" className="btn-gold h-11 px-6" data-testid="donate-page-submit">Send Donation Inquiry</Button>
          </div>
        </form>
      </section>
    </Layout>
  );
};
